/**
 * GlobalCommandPalette — ⌘K / Ctrl+K cross-suite search.
 * Mount at the App root. Triggered by keyboard or PremiumNav search button.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Search, Sparkles, Youtube, GraduationCap, Pen, FileText, Tag, Image as ImageIcon,
  BarChart3, Mic, Calendar, Users, Hash, Mail, Megaphone, Calculator,
  Palette, Key, Link as LinkIcon, Type, MessageSquare, BookOpen, LayoutGrid,
  Network, Clock, Brain, Target, FileQuestion, Award, CalendarCheck, PenTool,
  ClipboardList, Library, FolderKanban, LayoutDashboard, Settings, User, Command,
  Repeat, Upload, Newspaper, Database,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';

const COMMANDS = [
  // Suites
  { name: "Dashboard", section: "Suites", to: "/dashboard", Icon: LayoutDashboard, keywords: "home overview" },
  { name: "Workspace", section: "Suites", to: "/workspace", Icon: FolderKanban, keywords: "projects calendar tasks" },
  { name: "AI Studio", section: "Suites", to: "/ai-studio", Icon: Sparkles, keywords: "all ai tools" },
  { name: "Teaching Suite", section: "Suites", to: "/teaching", Icon: GraduationCap },
  { name: "YouTube Suite", section: "Suites", to: "/youtube-suite", Icon: Youtube },
  { name: "Content Suite", section: "Suites", to: "/content-suite", Icon: Pen },
  { name: "Reports", section: "Suites", to: "/reports", Icon: BarChart3 },

  // Hero tools
  { name: "Channel Assessment", section: "Hero tools", to: "/youtube-suite/channel-assessment", Icon: Youtube, keywords: "audit channel youtube" },
  { name: "AI Keyword Generator", section: "Hero tools", to: "/tools/seo/ai", Icon: Search, keywords: "serpapi keywords seo" },
  { name: "AI SEO Toolkit", section: "Hero tools", to: "/tools/seo/ai", Icon: Tag, keywords: "meta tags content grader" },
  { name: "AI PPT Generator", section: "Hero tools", to: "/tools/ai-slides", Icon: GraduationCap, keywords: "presentation slides pptx" },
  { name: "Auto Captions", section: "Hero tools", to: "/tools/auto-captions", Icon: Mic, keywords: "srt subtitles video" },

  // Teaching
  { name: "Teaching AI Assistant", section: "Teaching", to: "/teaching/ai-assistant", Icon: Sparkles, keywords: "upload pdf ppt lesson plan quiz homework bundle ai assistant book chapter" },
  { name: "My Bundles", section: "Teaching", to: "/teaching/bundles", Icon: Database, keywords: "saved bundles library lesson quiz" },
  { name: "Lesson Planner", section: "Teaching", to: "/teaching/lesson-planner", Icon: BookOpen },
  { name: "Question Paper", section: "Teaching", to: "/teaching/question-paper", Icon: ClipboardList },
  { name: "Question Bank", section: "Teaching", to: "/teaching/question-bank", Icon: FileQuestion },
  { name: "Worksheet Generator", section: "Teaching", to: "/teaching/worksheet", Icon: FileText },
  { name: "Flashcard Generator", section: "Teaching", to: "/teaching/flashcards", Icon: LayoutGrid },
  { name: "Mind Map Generator", section: "Teaching", to: "/teaching/mind-map", Icon: Network },
  { name: "PPT to Quiz", section: "Teaching", to: "/teaching/ppt-to-quiz", Icon: FileText, keywords: "upload pptx quiz" },
  { name: "Teaching Notes", section: "Teaching", to: "/teaching/teaching-notes", Icon: PenTool },
  { name: "Study Material", section: "Teaching", to: "/teaching/study-material", Icon: Library },
  { name: "Rubric Generator", section: "Teaching", to: "/teaching/rubric", Icon: Target },
  { name: "Homework Generator", section: "Teaching", to: "/teaching/homework", Icon: Clock },
  { name: "Assignment Generator", section: "Teaching", to: "/teaching/assignment", Icon: Brain },
  { name: "Attendance Sheet", section: "Teaching", to: "/teaching/attendance", Icon: CalendarCheck },
  { name: "Certificate Generator", section: "Teaching", to: "/teaching/certificate", Icon: Award },

  // YouTube tools
  { name: "YouTube SEO Tools", section: "YouTube", to: "/tools/youtube", Icon: Youtube, keywords: "title description tags hashtag" },
  { name: "Competitor Analysis", section: "YouTube", to: "/youtube-suite/competitor-analysis", Icon: Users, keywords: "compare channels benchmark" },
  { name: "Thumbnail Brief", section: "YouTube", to: "/youtube-suite/thumbnail-brief", Icon: ImageIcon, keywords: "thumbnail design concept" },
  { name: "Video Repurpose", section: "YouTube", to: "/youtube-suite/video-repurpose", Icon: Repeat, keywords: "reels twitter blog repurpose" },
  { name: "Upload Planner", section: "YouTube", to: "/youtube-suite/upload-planner", Icon: Upload, keywords: "publishing schedule calendar" },
  { name: "AI SEO Assistant (Gemini)", section: "YouTube", to: "/studio/seo", Icon: FileText, keywords: "gemini video seo" },
  { name: "Studio Analytics", section: "YouTube", to: "/studio/analytics", Icon: BarChart3 },
  { name: "Content Calendar", section: "YouTube", to: "/studio/calendar", Icon: Calendar },

  // Content
  { name: "Social Media Tools", section: "Content", to: "/tools/social-media", Icon: Megaphone, keywords: "instagram facebook linkedin twitter" },
  { name: "Press Release", section: "Content", to: "/content-suite/press-release", Icon: Newspaper, keywords: "pr news announce" },
  { name: "Case Study", section: "Content", to: "/content-suite/case-study", Icon: FileText, keywords: "client problem solution result" },
  { name: "AI Blog Generator", section: "Content", to: "/ai-blog", Icon: BookOpen },
  { name: "Content Tools", section: "Content", to: "/tools/content", Icon: FileText, keywords: "email newsletter product" },
  { name: "Google Ads Copy", section: "Content", to: "/tools/google-ads", Icon: Megaphone },
  { name: "Facebook Ads", section: "Content", to: "/tools/facebook-ads", Icon: Megaphone },
  { name: "AI Image Generator", section: "Content", to: "/ai-image", Icon: ImageIcon, keywords: "nano banana gpt image" },

  // Free tools
  { name: "Word Counter", section: "Free utilities", to: "/free/word-counter", Icon: Type },
  { name: "URL Shortener", section: "Free utilities", to: "/free/url-shortener", Icon: LinkIcon },
  { name: "Password Generator", section: "Free utilities", to: "/free/password-generator", Icon: Key },
  { name: "ROI Calculator", section: "Free utilities", to: "/free/roi-calculator", Icon: Calculator },
  { name: "Color Palette", section: "Free utilities", to: "/free/color-palette-generator", Icon: Palette },
  { name: "Hashtag Generator", section: "Free utilities", to: "/free/hashtag-generator", Icon: Hash },
  { name: "WhatsApp Generator", section: "Free utilities", to: "/free/whatsapp-generator", Icon: MessageSquare },

  // Settings
  { name: "Settings", section: "Account", to: "/settings/integrations", Icon: Settings },
  { name: "Profile", section: "Account", to: "/profile", Icon: User },
  { name: "Pricing", section: "Account", to: "/pricing", Icon: BarChart3 },
];

function fuzzyMatch(text, query) {
  if (!query) return { ok: true, score: 0 };
  const q = query.toLowerCase().trim();
  const t = text.toLowerCase();
  // Exact start (highest)
  if (t.startsWith(q)) return { ok: true, score: 100 };
  // Word-boundary start
  if (new RegExp("\\b" + q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).test(t)) return { ok: true, score: 80 };
  // Substring
  if (t.includes(q)) return { ok: true, score: 50 };
  // Subsequence fallback
  let qi = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++;
  }
  return qi === q.length ? { ok: true, score: 10 } : { ok: false, score: 0 };
}

export default function GlobalCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [contentHits, setContentHits] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Debounced cross-suite content search
  useEffect(() => {
    if (!query || query.length < 2) { setContentHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/premium/search`, {
          params: { q: query, limit: 8 },
          withCredentials: true,
          timeout: 6000,
        });
        setContentHits(r.data?.items || []);
      } catch (_e) { setContentHits([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  // Open via Cmd+K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      const isOpen = e.key === "k" && (e.metaKey || e.ctrlKey);
      if (isOpen) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      // Also expose a global event so PremiumNav search button can toggle
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const onCustom = () => setOpen(true);
    window.addEventListener("mm:open-command-palette", onCustom);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mm:open-command-palette", onCustom);
    };
  }, []);

  // Reset state when opened
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Close on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  const filtered = useMemo(() => {
    if (!query) {
      const grouped = {};
      COMMANDS.forEach((c) => {
        if (!grouped[c.section]) grouped[c.section] = [];
        grouped[c.section].push(c);
      });
      return grouped;
    }
    // Score every command, then sort by score (name > keywords > section)
    const scored = COMMANDS.map((c) => {
      const nameScore = fuzzyMatch(c.name, query).score;
      const kwScore = c.keywords ? fuzzyMatch(c.keywords, query).score * 0.6 : 0;
      const secScore = fuzzyMatch(c.section, query).score * 0.3;
      const best = Math.max(nameScore, kwScore, secScore);
      return { c, best };
    }).filter((x) => x.best > 0).sort((a, b) => b.best - a.best);
    const grouped = {};
    scored.forEach(({ c }) => {
      if (!grouped[c.section]) grouped[c.section] = [];
      grouped[c.section].push(c);
    });
    return grouped;
  }, [query]);

  const flatList = useMemo(() => Object.values(filtered).flat(), [filtered]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flatList.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = flatList[active];
      if (target) navigate(target.to);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[100] bg-[#111111]/40 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4"
          onClick={() => setOpen(false)}
          data-testid="command-palette"
        >
          <motion.div
            initial={{ y: -12, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -12, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 380, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
              <Search className="w-5 h-5 text-slate-400"/>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search tools, suites, settings…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                onKeyDown={onKeyDown}
                className="flex-1 outline-none bg-transparent text-base font-sans placeholder-slate-400"
                data-testid="cmd-input"
              />
              <kbd className="hidden sm:inline px-2 py-1 text-[10px] bg-slate-100 border border-slate-200 rounded font-mono text-slate-500">esc</kbd>
            </div>

            <div className="max-h-[60vh] overflow-y-auto py-2">
              {flatList.length === 0 && contentHits.length === 0 && (
                <div className="px-5 py-8 text-center text-sm text-slate-500">
                  No results. Try different keywords.
                </div>
              )}

              {contentHits.length > 0 && (
                <div data-testid="cmd-content-hits">
                  <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#A855F7] font-sans flex items-center gap-1">
                    <Database className="w-3 h-3"/> Your past generations · {contentHits.length}
                  </div>
                  {contentHits.map((h, i) => (
                    <button
                      key={`hit-${i}`}
                      onClick={() => navigate(h.to)}
                      data-testid={`cmd-content-hit-${i}`}
                      className="w-full flex items-center gap-3 px-5 py-2.5 text-left text-slate-700 hover:bg-brand-50/40"
                    >
                      <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-brand-50 text-[#A855F7]">
                        <Database className="w-3.5 h-3.5"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{h.label || h.tool}</div>
                        <div className="text-[11px] text-slate-400 truncate">{h.section} · {h.tool}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {Object.entries(filtered).map(([section, items]) => (
                <div key={section}>
                  <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 font-sans">
                    {section}
                  </div>
                  {items.map((it) => {
                    const idx = flatList.indexOf(it);
                    const isActive = idx === active;
                    return (
                      <button
                        key={it.name}
                        onClick={() => navigate(it.to)}
                        onMouseEnter={() => setActive(idx)}
                        data-testid={`cmd-item-${it.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${isActive ? "bg-brand-50 text-[#7E22CE]" : "text-slate-700 hover:bg-slate-50"}`}
                      >
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${isActive ? "bg-[#A855F7] text-white" : "bg-slate-100 text-slate-500"}`}>
                          <it.Icon className="w-3.5 h-3.5"/>
                        </div>
                        <span className="text-sm font-medium flex-1">{it.name}</span>
                        {isActive && <span className="text-xs text-slate-500">↵ open</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between text-[11px] text-slate-500">
              <span className="flex items-center gap-3">
                <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono">↑↓</kbd> navigate</span>
                <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono">↵</kbd> select</span>
              </span>
              <span className="flex items-center gap-1"><Command className="w-3 h-3"/>K to toggle</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
